import {
  CreditNoteSchema,
  CreditNoteStatusType,
  ZodCreateCreditNoteSchema,
  ZodEditCreditNoteSchema,
  ZodGetCreditNotesParams,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type CreditNote = CreditNoteSchema;
export type CreditNoteImageType = "logo" | "signature";

export const getCreditNote = async (creditNoteId: string): Promise<CreditNote> => {
  const response = await apiClient.get<{ creditNote: CreditNote }>(`/api/credit-note/${creditNoteId}`);
  return response.data.creditNote;
};

/**
 * Get credit notes - returns all credit notes (for backward compatibility)
 * @deprecated Use getCreditNotesPaginated for better performance with large datasets
 */
export const getCreditNotes = async (params?: ZodGetCreditNotesParams): Promise<CreditNote[]> => {
  const response = await apiClient.get<{ creditNotes: CreditNote[]; pagination: PaginationMeta }>("/api/credit-note", {
    params,
  });
  return response.data.creditNotes;
};

/**
 * Paginated response for credit notes
 */
export interface GetCreditNotesPaginatedResponse {
  creditNotes: CreditNote[];
  pagination: PaginationMeta;
}

/**
 * Get credit notes with pagination support
 */
export const getCreditNotesPaginated = async (
  params?: ZodGetCreditNotesParams & PaginationParams,
): Promise<GetCreditNotesPaginatedResponse> => {
  const response = await apiClient.get<GetCreditNotesPaginatedResponse>("/api/credit-note", { params });
  return response.data;
};

export const createCreditNote = async (
  creditNoteData: ZodCreateCreditNoteSchema,
): Promise<{ success: boolean; message: string; creditNoteId: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string; creditNoteId: string }>(
    "/api/credit-note",
    creditNoteData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateCreditNote = async (
  creditNoteData: ZodEditCreditNoteSchema,
): Promise<{ success: boolean; message: string; creditNote: CreditNote }> => {
  const response = await apiClient.put<{ success: boolean; message: string; creditNote: CreditNote }>(
    `/api/credit-note/${creditNoteData.id}`,
    creditNoteData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateCreditNoteStatus = async (
  id: string,
  status: CreditNoteStatusType,
): Promise<{ success: boolean; message: string; creditNote: CreditNote }> => {
  const response = await apiClient.patch<{ success: boolean; message: string; creditNote: CreditNote }>(
    `/api/credit-note/${id}/status`,
    { status },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const deleteCreditNote = async (id: string): Promise<{ message: string; creditNote: CreditNote }> => {
  const response = await apiClient.delete<{ message: string; creditNote: CreditNote }>(`/api/credit-note/${id}`);
  return response.data;
};

export const generateCreditNoteNumber = async (relationId: string): Promise<{ creditNoteNumber: string }> => {
  const response = await apiClient.post<{ creditNoteNumber: string }>(
    "/api/credit-note/generate-number",
    { relationId },
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
};

export interface SaveAndSendCreditNoteData {
  id?: string;
  creditNoteData: ZodCreateCreditNoteSchema;
  emailData: {
    to: string;
    cc?: string | null;
    bcc?: string | null;
    subject: string;
    content: string;
    pdfContent: string;
  };
}

export const saveAndSendCreditNote = async (data: SaveAndSendCreditNoteData): Promise<{ message: string }> => {
  const response = await apiClient.post<{ message: string }>("/api/credit-note/save-and-send", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Fetch all credit notes by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllCreditNotes = async (params?: ZodGetCreditNotesParams): Promise<CreditNote[]> => {
  const allCreditNotes: CreditNote[] = [];
  let cursor: string | null = null;

  do {
    const response = await getCreditNotesPaginated({ ...params, cursor, limit: 100 });
    allCreditNotes.push(...response.creditNotes);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allCreditNotes;
};
